/**
 * cortex-admin の SNS コンテンツ生成 API contract（zod schema + 型）。
 * API（apps/api/admin）と Web（apps/web/cortex-admin）が共有する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNS コンテンツ生成のテーマ/タイトル生成 API ↔ Web 型を共有し、二重定義を排除する。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * テーマ生成リクエスト。編集画面に既に表示されているテーマ名を除外対象として渡す。
 * `accountId` はマルチアカウント対応で追加した optional 項目。未指定なら既存アカウント
 * （`adult-akanuke-laboratory`）へ Python 側がフォールバックする（画面を壊さないため）。
 * @graph-connects none
 */
export const GenerateThemesRequestSchema = z.object({
    excludeThemes: z.array(z.string()),
    accountId: z.string().optional(),
});
/** 生成された1件のテーマ（タイトルはまだ無い）。 @graph-connects none */
export const GeneratedThemeSchema = z.object({
    theme: z.string(),
});
/** テーマ生成レスポンス。 @graph-connects none */
export const GenerateThemesResponseSchema = z.object({
    themes: z.array(GeneratedThemeSchema),
});
/** タイトル生成リクエスト。テーマ1件につき1回呼び出す。 @graph-connects none */
export const GenerateTitleRequestSchema = z.object({
    theme: z.string().min(1),
    accountId: z.string().optional(),
});
/** タイトル生成レスポンス。 @graph-connects none */
export const GenerateTitleResponseSchema = z.object({
    title: z.string(),
});
/** キャプション生成リクエスト。テーマ・タイトル1件につき1回、投稿文とハッシュタグをまとめて呼び出す。 @graph-connects none */
export const GenerateCaptionRequestSchema = z.object({
    theme: z.string().min(1),
    title: z.string().min(1),
    accountId: z.string().optional(),
});
/** キャプション生成レスポンス。 @graph-connects none */
export const GenerateCaptionResponseSchema = z.object({
    postText: z.string(),
    hashtags: z.array(z.string()),
});
/** 画像プロンプト生成リクエスト。テーマ・タイトル1件につき1回、全ページ分をまとめて呼び出す。 @graph-connects none */
export const GeneratePagePromptsRequestSchema = z.object({
    theme: z.string().min(1),
    title: z.string().min(1),
    pageCount: z.number().int().min(1),
    accountId: z.string().optional(),
});
/** 生成された1ページ分の画像プロンプト（画像自体はまだ無い）。 @graph-connects none */
export const GeneratedPagePromptSchema = z.object({
    pageNumber: z.string(),
    imagePrompt: z.string(),
});
/** 画像プロンプト生成レスポンス。`contentId` は後続の generateImage / recordContentDownload で使う。 @graph-connects none */
export const GeneratePagePromptsResponseSchema = z.object({
    contentId: z.string(),
    pages: z.array(GeneratedPagePromptSchema),
});
/** 画像生成リクエスト。ページ1枚につき1回呼び出す。 @graph-connects none */
export const GenerateImageRequestSchema = z.object({
    contentId: z.string().min(1),
    pageNumber: z.string().min(1),
    accountId: z.string().optional(),
});
/** 画像生成レスポンス。GCS 原本バケットの V4 署名付き URL。 @graph-connects none */
export const GenerateImageResponseSchema = z.object({
    imageUrl: z.string(),
});
/**
 * ダウンロード記録リクエスト。画像を実際にダウンロードしたときだけ呼び、次回以降の
 * テーマ生成で除外対象にする（生成しただけで使わなかったテーマは除外対象にしない）。
 * @graph-connects none
 */
export const RecordContentDownloadRequestSchema = z.object({
    contentId: z.string().min(1),
    accountId: z.string().optional(),
});
/** ダウンロード記録レスポンス。 @graph-connects none */
export const RecordContentDownloadResponseSchema = z.object({});
/**
 * TikTok認証URL発行リクエスト。PKCEのcode_verifier/stateはPython側が生成しFirestoreへ
 * 短命に保管するため、ここでは連携先アカウントの `accountId` だけを受け渡す。
 * @graph-connects none
 */
export const TiktokAuthUrlRequestSchema = z.object({
    accountId: z.string().optional(),
});
/** TikTok認証URL発行レスポンス。 @graph-connects none */
export const TiktokAuthUrlResponseSchema = z.object({
    authorizeUrl: z.string(),
});
/**
 * TikTok OAuth callbackリクエスト。ブラウザのリダイレクト後に受け取ったcode/stateを中継する。
 * @graph-connects none
 */
export const TiktokCallbackRequestSchema = z.object({
    code: z.string().min(1),
    state: z.string().min(1),
});
/** TikTok OAuth callbackレスポンス。 @graph-connects none */
export const TiktokCallbackResponseSchema = z.object({});
/**
 * TikTok下書き送信リクエスト。`createdBy`はadmin-apiがEdge Authのユーザーメールを補うため
 * このリクエストには含めない（generatePagePromptsと同じ形）。
 * @graph-connects none
 */
export const TiktokCreateDraftRequestSchema = z.object({
    contentId: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    accountId: z.string().optional(),
});
/** TikTok下書き送信レスポンス。ポーリングはここでは行わず、状態確認は別procedureに分ける。 @graph-connects none */
export const TiktokCreateDraftResponseSchema = z.object({
    publishId: z.string(),
});
/** TikTok下書きステータス確認リクエスト。 @graph-connects none */
export const TiktokGetStatusRequestSchema = z.object({
    publishId: z.string().min(1),
    accountId: z.string().optional(),
});
/** TikTok下書きステータス確認レスポンス。 @graph-connects none */
export const TiktokGetStatusResponseSchema = z.object({
    status: z.string(),
});
//# sourceMappingURL=sns-content-generator.js.map